import { Component, ElementRef, HostListener, Input, OnDestroy, ViewChild, Type } from '@angular/core';
import { tabs, ITab, dictResponseCls } from './lookup';
import { createGuid } from 'src/app/core/common';
import { DocumentManagerService } from 'src/app/services/document/document-manager.service';
import { AsgardDocHtml } from 'src/app/core/documents/asgard-doc-html';
import { HtmlInstanceManagerService } from 'src/app/services/instance/html-instance-manager.service';
import { ReadingService } from 'src/app/services/text/reading.service';
import { ReaderStatus, ReadingBase, ReadingMode } from 'src/app/core/reading-utils/enums';
import { ReadingActions, ModalWindowsTypes } from 'src/app/core/enums';
import { IModalWindow } from 'src/app/utils/interfaces/modal-dialog.interface';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { ReadingModesService } from 'src/app/services/reading-modes.service';
import { CurrentUIState } from 'src/models/UIState';
import { CurrentUiStoreService } from 'src/app/services/ui/current-ui-store.service';
import { TranslateModule } from '@ngx-translate/core';
import { CoreLookupComponent } from './core-lookup/core-lookup.component';
import { NgClass } from '@angular/common';
import { CopyToClipboardService } from 'src/app/services/copy-to-clipboard.service';

@Component({
    selector: 'asg-lookup-dialog',
    templateUrl: './lookup.component.html',
    styleUrls: ['./lookup.component.css'],
    standalone: true,
    imports: [NgClass, CoreLookupComponent, TranslateModule]
})
export class LookupComponent implements OnDestroy, IModalWindow {
  @Input() darkMode = false;

  @ViewChild('coreLkp', { read: ElementRef, static: false }) coreLookup: ElementRef;

  get activeTab(): ITab | undefined { return this.tabs.find(x => x.isActive) }
  get isPlaying(): boolean { return this.readingService.readingStatus === ReaderStatus.Speaking }

  readonly documentId: string = createGuid();
  readonly tabs: ITab[] = tabs.concat();
  showHmpDropdown = false;
  haveContentToRead = false;
  copied = false;
  copiedTimerId = 0;

  uiState: CurrentUIState;

  constructor(
    private readonly readingService: ReadingService,
    private readonly docMgrService: DocumentManagerService,
    private readonly stngsService: SettingsService,
    private readonly instanceMgrSvc: HtmlInstanceManagerService,
    private readonly readingModesService: ReadingModesService,
    private readonly uiService: CurrentUiStoreService,
    private readonly copyToClipboardService: CopyToClipboardService
  ) {
    this.subToUIState();
  }

  ngOnDestroy(): void {
    this.readingService.runAction(ReadingActions.Stop);
    this.docMgrService.removeDocument(this.documentId);
    clearTimeout(this.copiedTimerId);
  }

  getWindowType(): Type<IModalWindow> {
    return LookupComponent;
  }
  getWindowEnumType(): ModalWindowsTypes {
    return ModalWindowsTypes.LOOKUP_WINDOW;
  }

  getInstanceOf(type: string): IModalWindow | null {
    switch (type) {
      case 'IModalWindow':
        return this as unknown as IModalWindow;
      default: return null;
    }
  }

  selectTab(dstTab: ITab): void {
    if (this.activeTab && dstTab.key === this.activeTab.key) {
      return;
    }

    for (const itemTab of this.tabs) {
      if (itemTab.key === dstTab.key) {
        itemTab.isActive = true;
      } else {
        itemTab.isActive = false;
      }
    }
  }

  play(): void {
    if (this.isPlaying && this.docMgrService.currentDoc) {
      this.readingService.runAction(ReadingActions.Stop);
      this.docMgrService.currentDoc.clearTextHighlight(true);
      return;
    }

    this.readingService.runAction(ReadingActions.PlayPause);
  }

  onContentChanged(contentExists: boolean): void {
    this.haveContentToRead = contentExists;

    if (!!this.docMgrService.currentDoc && this.docMgrService.currentDoc.documentId === this.documentId) {
      this.docMgrService.removeDocument(this.documentId);
    }

    if (contentExists) {
      const newDoc = this.docMgrService.createFocusDocument(AsgardDocHtml, [dictResponseCls, false, true, this.documentId]);
      const validateClick = (evt: MouseEvent): { skip: boolean, rebuildTxtMap: boolean } => {
        const currSelection = document.getSelection();
        if (currSelection != null && currSelection.isCollapsed === false) {
          return { skip: true, rebuildTxtMap: true };
        }
        const isSummary = (evt.target as HTMLElement).closest('summary') !== null;
        return { skip: isSummary, rebuildTxtMap: isSummary };
      }
      this.instanceMgrSvc.createEditorInstace(newDoc, null, validateClick);
      this.readingModesService.setReadingModeForCurrentDoc(ReadingMode.ClickToPlay, ReadingBase.Continuous);
      newDoc.goToFirstItem(this.stngsService.settings.readingUnit);
    }
  }

  onHmpListClick(event: MouseEvent): void {
    this.showHmpDropdown = !this.showHmpDropdown;
    event.stopPropagation();
  }

  copyToClipboard(value: string): void {
    if (!value) {
      const coreLookupElement = this.coreLookup.nativeElement as HTMLElement;
      const lookupContentElement = coreLookupElement.querySelector('.lookup-output') as HTMLElement;
      value = lookupContentElement.innerText;
    }

    this.copyToClipboardService.copy(value)
    .then(() => {
      this.copied = true;
      this.copiedTimerId = window.setTimeout(() => {
        this.copied = false;
      }, 1000);
    })
    .catch(err => {
      console.log('Something went wrong for copy', err);
    })
  }

  @HostListener('document:click')
  onHideHomophones(): void {
    this.showHmpDropdown = false;
  }

  private subToUIState(): void {
    this.uiService.getUIState()
      .subscribe((e: CurrentUIState) => {
        this.uiState = e as CurrentUIState;
        this.checkActiveTab();
      });
  }

  private checkActiveTab(): void {
    const activeTab = this.tabs.find(t => t.isActive);
    if ((activeTab && activeTab.feature && this.uiState && this.uiState[activeTab.feature].locked) || (!activeTab && this.uiState)) {
      if (activeTab) {
        activeTab.isActive = false;
      }
      const tab = this.tabs.find(t => !!!t.feature || !!(this.uiState && !this.uiState[t.feature].locked));
      if (tab) {
        tab.isActive = true;
      }
    }
  }
}
